import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@radix-ui/react-label'
import { useMemo } from 'react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMutation } from '@tanstack/react-query'
import { updateOrganizationInfoOrganizationOrganizationIdUpdateInfoPutMutation } from '@/generated/@tanstack/react-query.gen'
import { useUser } from '@/pages/enterprise/user-provider'
import { statesArray } from '@/pages/subscribe/_data/subscribe-constant'
import Spinner from '@/components/Spinner'
import { toast } from '@/components/custom-toast'
import { isEqual } from 'lodash'
import { format, unformat, useMask } from '@react-input/mask'

const phoneRegex = /^\+1\s\(\d{3}\)\s\d{3}-\d{4}$/

const EnterpriseInformationSchema = z.object({
  billingAddress: z
    .string()
    .regex(
      /^[a-zA-Z0-9\s,.\-]*$/,
      'Billing address must not contain special characters.'
    )
    .optional(),
  billingState: z.string().optional(),
  billingCity: z.string().optional(),
  billingZipCode: z.number().optional(),
  mailingAddress: z
    .string()
    .regex(
      /^[a-zA-Z0-9\s,.\-]*$/,
      'Mailing address must not contain special characters.'
    )
    .optional(),
  mailingState: z.string().optional(),
  mailingCity: z.string().optional(),
  mailingZipCode: z.number().optional(),
  sameBillingAddress: z.boolean(),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(phoneRegex, 'Please enter a valid phone number'),
  email: z.string().optional(),
})

interface IProps {
  onCancel: () => void
  onSuccess: () => void
}

export function UpdateEnterpriseInformationForm({
  onCancel,
  onSuccess,
}: IProps) {
  const { organizationId, paymentEnterpriseInformation, onUpdateSeats } =
    useUser()

  const phoneNumberMask = {
    mask: '+1 (___) ___-____',
    replacement: { _: /\d/ },
  }

  const phoneInputRef = useMask(phoneNumberMask)
  let phoneNumber = paymentEnterpriseInformation?.phoneNumber
  phoneNumber = format(phoneNumber.replace('+1', ''), phoneNumberMask)

  const form = useForm({
    resolver: zodResolver(EnterpriseInformationSchema),
    defaultValues: {
      billingAddress: paymentEnterpriseInformation.billingAddress?.address,
      billingState: paymentEnterpriseInformation.billingAddress?.state,
      billingZipCode: paymentEnterpriseInformation.billingAddress?.zip_code,
      billingCity: paymentEnterpriseInformation.billingAddress?.city,
      mailingAddress: paymentEnterpriseInformation.mailingAddress?.address,
      mailingState: paymentEnterpriseInformation.mailingAddress?.state,
      mailingZipCode: paymentEnterpriseInformation.mailingAddress?.zip_code,
      mailingCity: paymentEnterpriseInformation.mailingAddress?.city,
      sameBillingAddress: isEqual(
        paymentEnterpriseInformation.billingAddress,
        paymentEnterpriseInformation.mailingAddress
      ),
      phoneNumber: phoneNumber,
      email: paymentEnterpriseInformation?.email,
    },
  })

  const isSameBillingAddress = form.watch('sameBillingAddress')

  const updateInfo = useMutation({
    ...updateOrganizationInfoOrganizationOrganizationIdUpdateInfoPutMutation(),
    onSuccess: () => {
      onUpdateSeats()
      onSuccess()
      toast.success({
        message: 'Enterprise information updated successfully.',
      })
    },
    onError: (error) => {
      toast.error({
        message: error.message,
      })
    },
  })

  const selectOptions = useMemo(
    () =>
      statesArray.map((state) => (
        <SelectItem key={state} value={state}>
          {state}
        </SelectItem>
      )),
    []
  )

  const getBillingAddress = (
    data: z.infer<typeof EnterpriseInformationSchema>
  ) => ({
    address: data.billingAddress,
    city: data.billingCity,
    state: data.billingState,
    zip_code: data.billingZipCode,
  })

  const getMailingAddress = (
    data: z.infer<typeof EnterpriseInformationSchema>
  ) => ({
    address: data.mailingAddress,
    city: data.mailingCity,
    state: data.mailingState,
    zip_code: data.mailingZipCode,
  })

  const onSubmit = (data: z.infer<typeof EnterpriseInformationSchema>) => {
    const unformattedPhone = unformat(data.phoneNumber, phoneNumberMask)
    const phoneWithCountryCode = unformattedPhone.startsWith('+1')
      ? unformattedPhone
      : `+1${unformattedPhone}`

    updateInfo.mutate({
      path: { organization_id: organizationId },
      body: {
        phone_number: phoneWithCountryCode,
        email: data.email,
        ...(data.sameBillingAddress
          ? {
              mailing_address: getBillingAddress(data),
            }
          : {
              mailing_address: getMailingAddress(data),
            }),

        billing_address: getBillingAddress(data),
      },
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="billingAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Billing Address</FormLabel>
              <FormControl>
                <Input
                  className="h-14"
                  placeholder="Address (Street/House Number)"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="billingState"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="h-14">
                      <SelectValue placeholder="State/Region" />
                    </SelectTrigger>
                    <SelectContent>{selectOptions}</SelectContent>
                  </Select>
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="billingCity"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="City"
                    className="md:col-span-1 h-14"
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="billingZipCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="number"
                    defaultValue={field.value}
                    placeholder="ZIP/Postal"
                    className="md:col-span-1 h-14"
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      return field.onChange(value)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="sameBillingAddress"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">Mailing Address</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sameBillingAddress"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label htmlFor="billingAddress">
                      Same as Billing Address
                    </Label>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isSameBillingAddress && (
          <>
            <FormField
              disabled={isSameBillingAddress}
              control={form.control}
              name="mailingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className="h-14"
                      placeholder="Address (Street/House Number)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />{' '}
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 md:grid-cols-3 gap-4">
              <FormField
                disabled={isSameBillingAddress}
                control={form.control}
                name="mailingState"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger
                          disabled={isSameBillingAddress}
                          className="h-14"
                        >
                          <SelectValue placeholder="State/Region" />
                        </SelectTrigger>
                        <SelectContent>{selectOptions}</SelectContent>
                      </Select>
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                disabled={isSameBillingAddress}
                control={form.control}
                name="mailingCity"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="City"
                        className="md:col-span-1 h-14"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                disabled={isSameBillingAddress}
                control={form.control}
                name="mailingZipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        defaultValue={field.value}
                        placeholder="ZIP/Postal"
                        className="md:col-span-1 h-14"
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          return field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    ref={phoneInputRef}
                    className="h-14"
                    placeholder="Phone number"
                    type="tel"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    className="h-14"
                    placeholder="info@hydrascore.com"
                    type="email"
                    {...form.register('email')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-4">
          <Button type="submit">Save Changes</Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
      {updateInfo.isPending && <Spinner />}
    </Form>
  )
}
